import ArrowDownwardRoundedIcon from '@material-ui/icons/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@material-ui/icons/ArrowUpwardRounded';
import { addDays, format } from 'date-fns';
import { memo } from 'react';
import CurrencyFormat from 'react-currency-format';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { GerenciadorPrecos } from '../../../../../../@types/GerenciadorPrecos';
import { BaseCell } from '../../../../../../components';
import { AlertWithTimestamp } from '../../../../../../components/AlertWithTimestamp';
import { useCanAccessDemandForecast } from '../../../../../../hooks/useCanAccessDemandForecast';
import { UPDATE_GERENCIADOR_DATAPOINT_BY_INDEX } from '../../../../../../reducers/gerenciadorPrecos/datapoints';
import { SET_DATA, SET_INDEX, SET_IS_OPEN } from '../../../../../../reducers/ipa/priceManager/modals/demandForecast';
import { getDemandForecastSimpleOutputs } from '../../../../SimulacoesAnalises/PrevisaoDemanda/v2/services';
import CalculateDemandForecastButton from './CalculateDemandForecastButton';

type RowData = GerenciadorPrecos.RowData;

type DemandForecastCellProps = {
  rowData: RowData;
  index: number;
} & ReturnType<typeof mapDispatchToProps>;

const DemandForecastCell = memo(({ rowData, index, setData, setIndex, openModal, updateDatapointByIndex }: DemandForecastCellProps) => {
  const canAccessDemandForecast = useCanAccessDemandForecast();

  const handleOpenModal = () => {
    if (!canAccessDemandForecast) return;
    if (rowData.novoCalculo || !rowData?.demandForecast) return;
    setData(rowData.demandForecast);
    setIndex(index);
    openModal();
  };

  const handleCalculateDemandForecast = async () => {
    if (!canAccessDemandForecast) return;

    const updatedRowData: Partial<RowData> = {
      loadingCalc: true,
    };

    updateDatapointByIndex(index, updatedRowData);

    try {
      const date = [format(new Date(), 'yyyy/MM/dd'), format(addDays(new Date(), 27), 'yyyy/MM/dd')];

      const query = {
        product_id: rowData.productId,
        product_name: rowData.description,
        store_ids: [rowData.storeId],
        from_date: new Date(date[0]),
        to_date: new Date(date[1]),
        new_price: rowData.price,
      };
      const input = {
        base_price: rowData.retailPrice,
        competitiveness: 0,
        margin: 0,
        new_price: rowData.price,
      };
      const data = await getDemandForecastSimpleOutputs({
        input: [input],
        query,
      });

      if (data) {
        updatedRowData.demandForecast = data;
        updatedRowData.novoCalculo = false;
      }

      updatedRowData.loadingCalc = false;

      updateDatapointByIndex(index, updatedRowData);
    } catch {
      AlertWithTimestamp({ message: 'Erro ao calcular previsão' });
    }
  };

  return (
    <BaseCell onClick={handleOpenModal}>
      <div className="previsao-demanda">
        {rowData.novoCalculo ? (
          <span className="calculo">
            <CalculateDemandForecastButton isLoading={!!rowData.loadingCalc} canAccess={canAccessDemandForecast} onCalculate={handleCalculateDemandForecast} />
          </span>
        ) : rowData.demandForecast?.new_price_scenario?.revenue_forecast && !rowData.novoCalculo ? (
          <>
            <CurrencyFormat
              className="currency"
              fixedDecimalScale
              decimalScale={2}
              value={rowData?.demandForecast?.new_price_scenario?.revenue_forecast}
              displayType="text"
              prefix="R$ "
              decimalSeparator=","
              thousandSeparator="."
            />
            {rowData?.demandForecast?.new_price_scenario?.revenue_forecast > rowData.demandForecast?.base_price_scenario?.revenue_forecast ? (
              <ArrowUpwardRoundedIcon
                className="positive"
                style={{
                  fontSize: 16,
                }}
              />
            ) : rowData?.demandForecast?.new_price_scenario?.revenue_forecast < rowData.demandForecast?.base_price_scenario?.revenue_forecast ? (
              <ArrowDownwardRoundedIcon
                className="negative"
                style={{
                  fontSize: 16,
                }}
              />
            ) : null}
          </>
        ) : (
          '--'
        )}
      </div>
    </BaseCell>
  );
});

DemandForecastCell.displayName = 'DemandForecastCell';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setData: (data: GerenciadorPrecos.RowData['demandForecast'] | null) => dispatch(SET_DATA(data)),
  setIndex: (index: number | null) => dispatch(SET_INDEX(index)),
  openModal: () => dispatch(SET_IS_OPEN(true)),
  updateDatapointByIndex: (index: number, data: Partial<RowData>) => dispatch(UPDATE_GERENCIADOR_DATAPOINT_BY_INDEX({ index, data })),
});

export default connect(null, mapDispatchToProps)(DemandForecastCell);
